var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
import { openUrlWithPost } from '@shared/utils/openUrlWithPost';
import ExternalScreenTemplate from '@front/templates/ExternalScreenTemplate.html';
import { TokenPostExtLink } from '@shared/commons/cache/ExternalLink/TokenPostExtLink';
import { WebContainerExtLink } from '@shared/commons/cache/ExternalLink/WebContainerExtLink';
import { Connection } from '@shared/commons/Connection';
import { DataCache } from '@shared/commons/DataCache';
import { DynProperty } from '@shared/commons/DynProperty';
import { ResourcesManager } from '@shared/commons/properties/ResourcesManager';
import { CurrentLang, LOCALE_ES, Resources } from '@shared/localizations/Resources';
import { externalScreenHandler } from '@shared/utils/AppHandlers';
import { Themes } from '@shared/utils/ThemesEnum';
import { DockPlaceConst, DockSystemInstance } from '../DockSystem';
import { ThemeManager } from '../misc/ThemeManager';
import { ApplicationPanel } from '../panels/ApplicationPanel';
import { PanelNames } from '../UtilsClasses/FactoryConstants';
import { MainWindowManager } from '../UtilsClasses/MainWindowManager';
import { WorkSpaceManager } from '../WorkSpace/WorkSpaceManager';
export class ExternalScreen extends ApplicationPanel {
    constructor() {
        super(...arguments);
        this.PanelContainer = null;
    }
    getType() {
        return PanelNames.ExternalScreen;
    }
    oncomplete() {
        super.oncomplete();
        this.deserializeDelayedData();
        const iframe = this.find('iframe');
        if (!isNullOrUndefined(iframe)) {
            iframe.onload = () => {
                void this.set('loading', false);
            };
            this.center();
        }
        else {
            void this.set('loading', false);
        }
        ResourcesManager.onLocaleChange.Subscribe(this.localize, this);
    }
    dispose() {
        ResourcesManager.onLocaleChange.UnSubscribe(this.localize, this);
        super.dispose();
    }
    localize() {
        var _a, _b;
        super.localize();
        const aliases = this.get('aliases');
        const aliasToolName = (_a = aliases === null || aliases === void 0 ? void 0 : aliases.get(CurrentLang)) === null || _a === void 0 ? void 0 : _a.toolName;
        void this.set('headerText', aliasToolName !== null && aliasToolName !== void 0 ? aliasToolName : this.get('headerText'));
        void this.set('header', (_b = this.get('headerText')) !== null && _b !== void 0 ? _b : Resources.getResource('External panel'));
    }
    AttachClick(_event) {
        void this.set({ dockablePanel: true, showHeader: false });
        DockSystemInstance.addPanelOnDock(this, DockPlaceConst.Right, false);
        WorkSpaceManager.currentWorkspace.addPanel(this);
    }
    CustomPreparation() {
        void this.set({ dockablePanel: true, showHeader: false });
    }
    callBack(properties) {
        super.callBack(properties);
        const updateProperty = (name) => {
            const dp = DynProperty.getPropertyByName(properties, name);
            if ((dp === null || dp === void 0 ? void 0 : dp.value) != null)
                void this.set(name, dp.value);
        };
        ExternalScreen.PROPERTY_NAMES.forEach(updateProperty);
        this.UpdateAfterLoad();
    }
    Properties() {
        const properties = super.Properties();
        ExternalScreen.PROPERTY_NAMES.forEach((name) => {
            const value = this.get(name);
            if (value != null)
                properties.push(new DynProperty(name, value, DynProperty.STRING, DynProperty.HIDDEN_GROUP));
        });
        return properties;
    }
    // #endregion
    UpdateAfterLoad() {
        return __awaiter(this, void 0, void 0, function* () {
            const { headerText, clientTab, originalUrl, ExternalLinkId } = this.get();
            let tool = null;
            if (headerText != null && clientTab != null) {
                tool = MainWindowManager.MainWindow.ExternalScreenStorage.GetTool(headerText, clientTab);
            }
            if (tool === null) {
                tool = DataCache.ExternalLinksCache.getLinkById(ExternalLinkId);
            }
            if (tool !== null) {
                const externalLink = tool.getUrlForExternalBrowser();
                this.UpdateLink(externalLink, tool.IsNoneLink(), tool);
                void this.set({ originalUrl: externalLink });
                MainWindowManager.MainWindow.ExternalScreenStorage.AddScreenItem(tool, this);
            }
            else if (isValidString(originalUrl)) {
                this.UpdateLink(originalUrl);
            }
        });
    }
    webContainerCorrectingActions(tool) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a, _b;
            yield this.resetPartial('bodyPartial', this.makeNewPartial(tool));
            this.center();
            const attached = this.get('dockablePanel');
            if (!attached) {
                const ic = this.find('.js-win-innerContainer');
                const divs = ic.getElementsByTagName('div');
                const [firstCh, contentCh] = divs;
                setTimeout(() => {
                    if (contentCh.style.width === '100%' && contentCh.style.height === '100%') {
                        firstCh.classList.add('iframe-container-widget');
                        void this.set({
                            width: 1024,
                            height: 600,
                            resizable: true
                        });
                    }
                    else {
                        void this.set({ width: null, height: null });
                    }
                }, 500);
            }
            const { width, height } = (_a = this.PanelContainer) !== null && _a !== void 0 ? _a : {};
            (_b = this.PanelContainer) === null || _b === void 0 ? void 0 : _b.resize(width, height); // init resize content
        });
    }
    themeChange() {
        super.themeChange();
        this.UpdateAfterLoad();
    }
    makeNewPartial(extLink) {
        return `<div  {{#if .dockablePanel}} class="iframe-container-widget" {{/if}}>
                ${extLink.getExternalLinkWebContent(ThemeManager.themeValue === Themes.Dark)}
                </div>`;
    }
    UpdateLink(newUrl, cleanUrl = false, tool = null) {
        var _a, _b;
        const culture = CurrentLang === LOCALE_ES ? 'es-ES' : 'en-GB';
        let url = this.getRefreshURL != null ? this.getRefreshURL() : newUrl;
        if (!cleanUrl) {
            const token = Connection.vendor.loginParams.accessToken;
            url += `?token=${token}&theme=night&language=${culture}&platform=TraderEvolution`;
        }
        if (tool instanceof WebContainerExtLink) {
            void this.webContainerCorrectingActions(tool);
        }
        void this.set({ url });
        if (tool != null) {
            const dataToUpdate = {
                clientTab: tool.clientTab,
                id: tool.id,
                headerText: (_a = tool.name) !== null && _a !== void 0 ? _a : null,
                aliases: (_b = tool.aliases) !== null && _b !== void 0 ? _b : null
            };
            void this.set(dataToUpdate);
            if (tool.name != null || tool.aliases != null)
                this.localize();
        }
    }
    static Show(messageInfo_1) {
        return __awaiter(this, arguments, void 0, function* (messageInfo, cleanUrl = false) {
            const culture = CurrentLang === LOCALE_ES ? 'es-ES' : 'en-GB';
            let url = messageInfo.Text;
            const tool = messageInfo.Tool;
            const token = Connection.vendor.loginParams.accessToken;
            if (tool instanceof TokenPostExtLink) {
                const externalLink = tool.getUrlForExternalBrowser();
                const postData = yield tool.getPostData();
                openUrlWithPost(externalLink, postData);
                return;
            }
            else if (!(tool instanceof WebContainerExtLink)) {
                if (!cleanUrl) {
                    url += `?token=${token}&theme=night&language=${culture}&platform=TraderEvolution`;
                }
                const useInternalBrowser = Boolean(messageInfo.ClientUseInternalBrowser);
                if (!useInternalBrowser) {
                    window.open(url);
                    return null;
                }
            }
            return ExternalScreen.getExternalScreen(url, messageInfo, tool);
        });
    }
    static externalScreenHandlerInitialize() {
        externalScreenHandler.Show = ExternalScreen.Show;
    }
    static getExternalScreen(url, messageInfo, tool) {
        const scr = tool.isAttached() ? MainWindowManager.MainWindow.addAttachedExternalScreen() : new ExternalScreen();
        scr.set({
            url,
            headerText: messageInfo.ScreenName,
            aliases: messageInfo.Aliases,
            clientTab: messageInfo.ClientTab,
            originalUrl: messageInfo.Text,
            ExternalLinkId: tool.id
        });
        if (tool instanceof WebContainerExtLink) {
            scr.webContainerCorrectingActions(tool);
        }
        const { ScreenWidth: width, ScreenHeight: height, ResizableScreen: resizable } = messageInfo;
        if (width > 0)
            scr.set({ width });
        if (height > 0)
            scr.set({ height });
        if (resizable !== undefined)
            scr.set({ resizable });
        if (messageInfo.GetRefreshURL != null) {
            scr.getRefreshURL = messageInfo.GetRefreshURL;
        }
        if (!tool.isAttached()) {
            MainWindowManager.AddControl(scr);
        }
        scr.setFocus();
        scr.localize();
        MainWindowManager.MainWindow.ExternalScreenStorage.AddScreenItem(tool, scr);
        return scr;
    }
}
// #region ICaller
ExternalScreen.PROPERTY_NAMES = ['headerText', 'clientTab', 'originalUrl', 'ExternalLinkId'];
ApplicationPanel.extendWith(ExternalScreen, {
    data: function () {
        return {
            width: 1024,
            height: 600,
            showFooter: false,
            resizable: false,
            url: null,
            headerText: null,
            aliases: null,
            loading: true,
            showHeader: true,
            dockablePanel: false,
            attached: false,
            canLinkByAccount: false,
            clientTab: '',
            originalUrl: '',
            ExternalLinkId: null,
            style_addition_header: 'attach-detach-panel-header'
        };
    },
    partials: { bodyPartial: ExternalScreenTemplate }
});
